// SpeechEngine の GPT-Live 実装 (spec 2026-09-14)。
//
// **半委任 (L7)** — 進行と文言はアプリが持ち、設問・案内・促しは session.instructions.append で
// 頼む。AI が話している間と、tool の結果を返した backend の続きが終わるまでは頼まずに待つ (§3.6)。
//
// **回答の記録は二重の網 (L8 / §3.5)** — live モデルに任せ、任せなかった・記録せずに終わった
// ときだけアプリが文字起こしを backend に直接渡す。受けるのは「今の設問の窓で、相手が話した後に
// 始まった依頼から来た、最初の record_answer」だけ。**回答をどの設問に付けるかはアプリが決める**
// (backend が返す question_id は使わない)。**事前チェックの氏名にも同じ網を張る** (§9.4) — 復唱に
// 「はい」と答えたのに記録が来なければ、アプリが復唱と返事を渡す (2026-09-14 の実機で止まったため)。
//
// **無音の出口 (L12)** は 2.1 と同じ — AI が話し終えたときだけ張り、相手が話し始めたら止める。
//
// ロジックは React から切り離した createLiveCore に閉じ、hook は状態の表示だけを持つ
// (タイマーとイベントの順序で決まる処理なので、再レンダーのたびに作り直さない)。

import { useMemo, useRef, useState } from "react";
import {
  LiveClient,
  liveDebug,
  type LiveBackendDone,
  type LiveDelegation,
  type LiveFunctionCall,
} from "../api/liveClient";
import { LiveTransport } from "../api/liveTransport";
import { AiSpeechTracker, type AiSpeechEvent } from "../audio/aiSpeechTracker";
import { liveAudioElement, unlockLiveAudio } from "../audio/liveAudioOutput";
import { MicPcmSource } from "../audio/micPcmSource";
import { mediaStreamManager } from "../audio/mediaStreamManager";
import { SilenceTimer } from "../audio/silenceTimer";
import { parseYesNo } from "../audio/yesNoIntent";
import type { Question } from "../survey/schema";
import type {
  EngineCallbacks,
  EngineState,
  NeedRepeatReason,
  SpeechEngine,
  StartOptions,
} from "../conversation/engine";
import { LIVE_BACKEND_MODEL, LIVE_MODEL } from "../conversation/liveModel";
import {
  buildAnswerFallbackMessage,
  buildNameConfirmMessage,
  buildNamePromptAppend,
  buildNarrationAppend,
  buildQuestionAppend,
  buildSilenceAppend,
} from "../conversation/liveInstructions";

const REASONS: NeedRepeatReason[] = ["no-content", "off-topic", "unclear"];
// cascade・2.1 と同じ待ち時間 (10 秒 × 2 段)。同じにしないと「どちらが待たせるか」が設定の差になる
const SILENCE_ESCALATIONS = [10_000, 10_000];

/** ① live モデルが話し始めてから任せるのを待つ時間。再解析: 任せるのは最後の入力の文字起こしから最大 2.0 秒 */
export const LIVE_DELEGATION_WAIT_MS = 3000;
/** ③ 最後の入力の文字起こしから、記録も聞き直しも無いまま待つ上限 (止まらないための最後の網) */
export const QUIET_FALLBACK_MS = 8000;
/** 依頼を送る前の待ち合わせの上限 (§3.6)。話し終えの判定が外れても止まらないため */
export const SEND_WAIT_MAX_MS = 8000;
/** tool の結果を返した後、backend の続きを待つ上限 */
export const CONTINUATION_WAIT_MS = 5000;
/** 読み上げを頼んでも話し始めなければ次へ (L10) */
export const NARRATION_START_TIMEOUT_MS = 8000;
/** アプリの依頼から、その delegation とみなす範囲。再解析: 0.16〜0.21 秒 (15/15) */
export const APP_DELEGATION_WINDOW_MS = 1500;
/** session.started を待つ上限 */
export const STARTED_TIMEOUT_MS = 10_000;
/** 受信音量を見る間隔 (L11) */
export const LEVEL_POLL_MS = 150;
/** stop の後、session.closed を待ってから接続を閉じる猶予 */
export const CLOSE_GRACE_MS = 1500;
/** 相手の発話が途切れたとみなす間 (画面の状態表示だけに使う) */
const USER_QUIET_MS = 1500;
/** 1 回答で溜める音声の上限 (16kHz × 120 秒)。越えた分は古い方から捨てる */
const MAX_TURN_SAMPLES = 16_000 * 120;

const SESSION_META: Record<string, unknown> = {
  engine: "live",
  liveModel: LIVE_MODEL,
  liveBackend: LIVE_BACKEND_MODEL,
};

/** 1 つの設問の窓 (spec §3.5) */
type Turn = {
  question: Question;
  /** 設問を読ませる追記を送ったか (送る前の入力は前の設問への発話の続き) */
  sent: boolean;
  appendEventId: string | null;
  /** 窓の開始 (サーバの時間軸)。追記が反映された位置 */
  openedAt: number | null;
  /** 窓の中で相手が最初に話した位置 (サーバの時間軸)。null = まだ話していない */
  userSpokeAt: number | null;
  heard: string;
  /** この窓の delegation と出どころ */
  delegations: Map<string, "live" | "app">;
  /** tool を呼んだ delegation */
  called: Set<string>;
  committed: boolean;
  /** 聞き直しを受けた / backend が記録を断った (相手が次に話すまでアプリの経路を出さない) */
  repeatHandled: boolean;
  /** 今の発話に対してアプリの経路を出した */
  fallbackSent: boolean;
  /** 相手の最後の発話の後に live モデルが任せた */
  liveSinceInput: boolean;
  pcm: Float32Array[];
  pcmSamples: number;
};

type Pending = {
  content: string;
  /** 送った後、相手の番になるか (true なら AI が話し終えた時点で無音の計測を張る) */
  expectUser: boolean;
  /** 読み上げるだけの案内か (読み終えたら onNarrationDone) */
  narration: boolean;
  /** 無音の段階を保つか (促し)。保たないと段階 0 だけが繰り返され、段階 1 に届かない */
  keepSilenceLevel?: boolean;
  onSent?: (eventId: string) => void;
};

/** tool の結果を返した backend の続き。tool を含んだ最初の応答の完了は数えない (skip) */
type Continuation = { until: number; skip: number };

/** 事前チェックの氏名への相手の返事 (氏名の二重の網、spec §9.4) */
type NameReply = {
  /** 相手が返事をした、案内役の発話 (= 復唱)。これと返事を backend に渡す */
  replyTo: string;
  heard: string;
  /** 返事の後に AI が話し始めた (次に相手が話したら、それは新しい返事) */
  aiResponded: boolean;
  /** 返事の後に live モデルが任せた delegation */
  liveDelegations: Set<string>;
  /** confirm_name / need_repeat が来た */
  settled: boolean;
  /** アプリが backend に頼んだ (頼むのは 1 回だけ。断られても頼み直さない) */
  appAsked: boolean;
};

type LiveCore = Pick<
  SpeechEngine,
  | "start"
  | "stop"
  | "pause"
  | "resume"
  | "setQuestion"
  | "narrate"
  | "askName"
  | "promptSilence"
  | "unlock"
>;

const toReason = (r: unknown): NeedRepeatReason =>
  REASONS.includes(r as NeedRepeatReason) ? (r as NeedRepeatReason) : "unclear";
const errMsg = (e: unknown): string => (e instanceof Error ? e.message : String(e)).slice(0, 200);

function merge(chunks: Float32Array[]): Float32Array {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Float32Array(total);
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.length;
  }
  return out;
}

function newTurn(q: Question): Turn {
  return {
    question: q,
    sent: false,
    appendEventId: null,
    openedAt: null,
    userSpokeAt: null,
    heard: "",
    delegations: new Map(),
    called: new Set(),
    committed: false,
    repeatHandled: false,
    fallbackSent: false,
    liveSinceInput: false,
    pcm: [],
    pcmSamples: 0,
  };
}

function createLiveCore(io: {
  cb: () => EngineCallbacks;
  setPhase: (s: EngineState) => void;
}): LiveCore {
  const cb = () => io.cb();
  const now = () => Date.now();

  let phase: EngineState = "idle";
  const setPhase = (s: EngineState) => {
    if (phase === s) return;
    phase = s;
    io.setPhase(s);
  };

  /** セッションの通し番号。止めた後に届いた古いセッションのイベントを、新しいセッションに混ぜない */
  let seq = 0;
  let transport: LiveTransport | null = null;
  let client: LiveClient | null = null;
  let silence: SilenceTimer | null = null;
  let mic: MicPcmSource | null = null;
  let purpose: "preflight" | "survey" = "survey";
  let started = false;
  let startedAt = 0;
  let onStarted: (() => void) | null = null;
  let turn: Turn | null = null;
  let pending: Pending | null = null;
  let expectUser = false;
  /** 最後に送った依頼が促しか。促しの後は無音の段階を保って数える (armSilence) */
  let keepSilenceLevel = false;
  let narration: { started: boolean } | null = null;
  let said = "";
  let appRequest: { eventId: string; at: number } | null = null;
  /**
   * 事前チェックの氏名の二重の網 (spec §9.4)。askName から、案内の読み上げへ進むまで。
   * live モデルが「はい」の後に任せずに止まった実機 (2026-09-14) の手当て
   */
  let nameStep = false;
  let nameReply: NameReply | null = null;
  /** AI が最後に話し終えた文 (復唱の候補) */
  let lastSaid = "";
  /** 氏名の step の delegation の出どころ (記録の経路 via を画面へ渡す) */
  const nameOrigins = new Map<string, "live" | "app">();
  const continuations = new Map<string, Continuation>();
  let paused = false;
  let usage = {
    reportedSeconds: 0,
    backendIn: 0,
    backendCachedIn: 0,
    backendOut: 0,
    backendResponses: 0,
  };
  let usageEmitted = false;
  const tracker = new AiSpeechTracker();
  let polling = false;
  let pagehide: (() => void) | null = null;

  type TimerKey = "t1" | "t3" | "pending" | "narration" | "speakStart" | "userQuiet" | "nameNet";
  const timers: Partial<Record<TimerKey, ReturnType<typeof setTimeout>>> = {};
  let poll: ReturnType<typeof setInterval> | null = null;
  const clear = (k: TimerKey) => {
    const h = timers[k];
    if (h !== undefined) clearTimeout(h);
    delete timers[k];
  };
  const later = (k: TimerKey, ms: number, f: () => void) => {
    clear(k);
    timers[k] = setTimeout(() => {
      delete timers[k];
      f();
    }, ms);
  };

  // ---- 無音 (L12) ----
  /** 相手が話し終えて、記録も聞き直しもまだ (二重の網の出番で、無音の出口は張らない) */
  const awaitingRecord = (): boolean =>
    !!turn && turn.userSpokeAt !== null && !turn.committed && !turn.repeatHandled;

  const armSilence = () => {
    // **pending と narration は変異検証で等価と判定した (2026-09-14)** — pending は送るまで最大
    // 8 秒で、送る (flush) ときに計測を止めるので、張っても 10 秒に届かない。narration は
    // expectUser = false の依頼にしか立たないので、!expectUser が先に弾く。どちらも「送った依頼の
    // 読み上げの間は数えない」の念のための網で、消すときは flush の silence.stop() と
    // narrate の expectUser: false が残っているかを確かめる
    if (!silence || !expectUser || pending || narration || awaitingRecord()) return;
    // **促しの後は段階を保つ (resume)** — start() は段階を 0 に戻すので、促しを読み終えた後にも
    // start() すると「聞こえていますでしょうか」(段階 0) だけが繰り返され、段階 1 (キーボード) に
    // 永久に届かない (cascade が 2026-07-25 に踏んだのと同じ形。SilenceTimer.resume() のコメント)
    if (keepSilenceLevel) silence.resume();
    else silence.start();
  };

  // ---- 依頼の待ち合わせ (§3.6) ----
  const continuationPending = (): boolean => {
    const n = now();
    for (const [id, c] of continuations) if (c.until <= n) continuations.delete(id);
    return continuations.size > 0;
  };
  const gatesOpen = () => !tracker.isSpeaking() && !continuationPending();

  const finishNarration = (why: "spoken" | "timeout") => {
    if (!narration) return;
    narration = null;
    clear("narration");
    liveDebug(`narration done (${why})`);
    cb().onNarrationDone?.();
  };

  const flush = () => {
    clear("pending");
    const p = pending;
    const c = client;
    if (!p || !c || !started) return;
    pending = null;
    // task が変われば「何を待っているか」も変わる。**stop であって reset ではない**
    // (reset は「沈黙から回復した」を意味する silence.recovery を出す。2.1 の setTask と同じ)
    silence?.stop();
    expectUser = p.expectUser;
    keepSilenceLevel = !!p.keepSilenceLevel;
    const id = c.appendInstructions(p.content);
    p.onSent?.(id);
    if (p.narration) {
      narration = { started: false };
      later("narration", NARRATION_START_TIMEOUT_MS, () => {
        if (narration && !narration.started) finishNarration("timeout");
      });
    } else if (p.expectUser) {
      // 頼んでも話し始めなければ、相手の番とみなして無音の出口を張る。止めた計測を張り直す
      // 契機は「AI が話し終えた」しか無いので、ここが無いと黙ったまま止まる (原則 5)
      later("speakStart", NARRATION_START_TIMEOUT_MS, armSilence);
    }
  };

  /** 送れるなら今送る。送れなければ待つ (上限 SEND_WAIT_MAX_MS) */
  const schedule = () => {
    if (!pending || !started) return;
    if (gatesOpen()) {
      flush();
      return;
    }
    if (timers.pending === undefined) later("pending", SEND_WAIT_MAX_MS, flush);
  };

  /** 次にやってほしいことは常に最新の 1 つ (待っている依頼は差し替える) */
  const request = (p: Pending) => {
    pending = p;
    schedule();
  };

  // ---- 二重の網 (§3.5) ----
  /**
   * 記録を待つのをやめる。頼み直すと堂々巡りになるので、相手が次に話すまでアプリの経路は
   * 出さず、黙っていても止まらないよう無音の出口を張る
   */
  const stopWaiting = (tn: Turn) => {
    tn.repeatHandled = true;
    clear("t3");
    if (phase === "thinking") setPhase("listening");
    armSilence();
  };

  const fallback = (why: "no-delegation" | "no-tool" | "quiet") => {
    const tn = turn;
    const c = client;
    // **committed は変異検証で等価と判定した (2026-09-14)** — 記録を受けると t1 / t3 を解き、
    // 完了 (②) も記録済みなら見ないので、記録の後にここへ来る経路が無い。消すときは onCall の
    // clear と onBackendDone の committed の判定が残っているかを確かめる。
    // **ここで網 (t1 / t3) を解かない** — 頼まずに戻る回 (もう頼んだ) で解くと、下で張る「待つ上限」
    // まで消え、アプリの依頼に応えが無いときの出口が無くなる。残った t1 が後で撃っても、ここで戻る
    if (!tn || !c || tn.committed || tn.repeatHandled || tn.fallbackSent) return;
    const heard = tn.heard.trim();
    // 何も聞いていないなら頼む中身が無い。**空白だけの断片は onInput が数えないので、今は
    // ここに来ない (変異検証で等価と判定、2026-09-14)** — onInput の判定を外すときの網
    if (!heard) return;
    tn.fallbackSent = true;
    const eventId = c.askBackend(buildAnswerFallbackMessage(tn.question.id, heard));
    appRequest = { eventId, at: now() };
    liveDebug(`fallback (${why})`, { questionId: tn.question.id });
    // アプリの依頼にも応えが無い (delegation が作られない・終わらない) ことがある。待ち続けると
    // 記録待ちのまま無音の出口も張られず止まるので、上限で待つのをやめる (原則 5)。
    // 中の `turn === tn && !tn.committed` は変異検証で等価と判定した (2026-09-14) — 記録を受けた
    // ときと次の設問へ移るときに t3 を解くので、古い設問や記録の後にここが撃つ経路は今は無い
    // (t3 を解く箇所を減らすときの網)
    later("t3", QUIET_FALLBACK_MS, () => {
      if (turn === tn && !tn.committed) stopWaiting(tn);
    });
  };

  /**
   * 記録にならなかった (tool を呼ばずに終えた・引数が壊れていた・値が空)。live の経路なら
   * アプリが頼み、アプリの経路なら待つのをやめる
   */
  const noRecord = (tn: Turn, origin: "live" | "app") => {
    if (origin === "live") fallback("no-tool");
    else stopWaiting(tn);
  };

  // ---- 氏名の二重の網 (事前チェック、spec §9.4) ----
  /**
   * 復唱に「はい」と答えたのに記録 (confirm_name) が来ないとき、アプリが backend に復唱と返事を渡す。
   * **頼むのは「復唱への、はい系の返事」のときだけ** — 名乗っただけ・否定・曖昧な返事で頼むと、
   * 復唱の確認を飛ばして記録させることになる。はい系かは端末側で決め (cascade の事前チェックと
   * 同じ parseYesNo)、復唱かは live への指示の「〜さん、ですね。」の形で見る
   */
  const nameFallback = (why: "no-delegation" | "no-tool" | "quiet") => {
    clear("nameNet");
    const r = nameReply;
    const c = client;
    // 見張りはこの 2 行に集めてある。**網のまわりの重ね掛けは変異検証で等価と判定した (2026-09-14)**
    // — ① を張る・③ を張り直す条件の settled / appAsked / origin、nameReply と組で見る nameStep
    // (氏名の step を抜けると nameReply を捨てる)、タイマーを畳む clear (ここ・記録が届いたとき・
    // narrate・後片付け) は、消しても発火した先のこの 2 行で止まる。nameStep / nameReply を戻す行は
    // narrate・askName・後片付けの 3 箇所で重なり、出どころ (nameOrigins) の消去は delegation の id が
    // 重ならないので効かない。**後片付けの lastSaid だけは効く** (戻さないと、始め直した後の返事を
    // 前のセッションの復唱への返事として頼む)。この 2 行を変えるときの網
    if (!r || !c || r.settled || r.appAsked) return;
    if (!r.replyTo.includes("ですね") || parseYesNo(r.heard) !== "yes") return;
    r.appAsked = true;
    const eventId = c.askBackend(buildNameConfirmMessage(r.replyTo, r.heard.trim()));
    appRequest = { eventId, at: now() };
    liveDebug(`name fallback (${why})`);
  };

  // ---- AI の話し始め・話し終え (L11) ----
  const onSpeech = (ev: AiSpeechEvent) => {
    if (ev === "start") {
      liveDebug("ai speech start");
      silence?.stop();
      clear("speakStart");
      setPhase("ai-speaking");
      if (narration && !narration.started) {
        narration.started = true;
        clear("narration");
      }
      const tn = turn;
      // ① live モデルが相手の番の終わりを判断して話し始めた。3 秒以内に任せなければアプリが頼む
      if (
        tn &&
        tn.userSpokeAt !== null &&
        !tn.committed &&
        !tn.repeatHandled &&
        !tn.fallbackSent &&
        !tn.liveSinceInput
      ) {
        later("t1", LIVE_DELEGATION_WAIT_MS, () => fallback("no-delegation"));
      }
      // ① (氏名) 返事の後に AI が話し始めたのに live モデルが任せていなければ、3 秒でアプリが頼む
      const r = nameReply;
      if (nameStep && r && !r.aiResponded) {
        r.aiResponded = true;
        if (!r.settled && !r.appAsked && r.liveDelegations.size === 0)
          later("nameNet", LIVE_DELEGATION_WAIT_MS, () => nameFallback("no-delegation"));
      }
      return;
    }
    if (ev === "end") {
      liveDebug("ai speech end");
      const text = said.trim();
      said = "";
      if (text) {
        lastSaid = text;
        // 何を話したかが見えないと、「記録しました」と言ったのに記録されない回の理由を追えない
        liveDebug("← narrated", text);
        cb().onNarrated?.(text);
      }
      setPhase(awaitingRecord() ? "thinking" : "listening");
      if (narration?.started) finishNarration("spoken");
      schedule();
      armSilence();
    }
  };

  // ---- data channel のイベント ----
  const onSessionStarted = () => {
    started = true;
    startedAt = now();
    const f = onStarted;
    onStarted = null;
    f?.();
  };

  /** 窓の開始 = こちらの追記がサーバの時間軸のどこに反映されたか */
  const onAppended = (clientEventId: string | null, startMs: number | null) => {
    const tn = turn;
    if (tn && clientEventId && tn.appendEventId === clientEventId && startMs !== null) {
      tn.openedAt = startMs;
    }
  };

  const onInput = (delta: string, startMs: number | null) => {
    // 空白だけの断片は発話とみなさない。数えると「話した」ことになって記録を待ち続けるのに、
    // 頼む中身が無いので二重の網も出ず、無音の出口も張られない (止まる)
    if (!delta.trim()) return;
    // 話し始めたら計測を戻す (張り直さない)。張り直すと話している最中にカウントが進む (2.1 と同じ)。
    // **reset であって stop ではない** — 相手が答えたので段階を 0 に戻し、段階を上げていた回は
    // silence.recovery を残す (2.1 の speech_started と同じ観測点)
    silence?.reset();
    setPhase("user-speaking");
    later("userQuiet", USER_QUIET_MS, () => {
      if (phase === "user-speaking") setPhase(awaitingRecord() ? "thinking" : "listening");
    });
    if (nameStep) {
      let r = nameReply;
      if (!r || r.aiResponded || r.settled) {
        // 返事の相手は、話している最中の発話 (話し終えの判定より先に答えることがある)、
        // 無ければ最後に話し終えた発話
        r = {
          replyTo: said.trim() || lastSaid,
          heard: "",
          aiResponded: false,
          liveDelegations: new Set(),
          settled: false,
          appAsked: false,
        };
        nameReply = r;
      }
      r.heard += delta;
      // ③ (氏名) 何も起きなければ 8 秒でアプリが頼む
      later("nameNet", QUIET_FALLBACK_MS, () => nameFallback("quiet"));
    }
    const tn = turn;
    if (!tn || !tn.sent || tn.committed) return;
    // 窓より前 (前の設問への発話の続き) は数えない
    if (tn.openedAt !== null && startMs !== null && startMs < tn.openedAt) return;
    if (tn.userSpokeAt === null) tn.userSpokeAt = startMs ?? tn.openedAt ?? 0;
    tn.heard += delta;
    tn.repeatHandled = false;
    tn.fallbackSent = false;
    tn.liveSinceInput = false;
    clear("t1");
    later("t3", QUIET_FALLBACK_MS, () => fallback("quiet"));
  };

  const onOutput = (delta: string) => {
    said += delta;
    onSpeech(tracker.feedTranscript(now()));
  };

  const onDelegation = (d: LiveDelegation) => {
    let origin: "live" | "app" = "live";
    const req = appRequest;
    if (
      req &&
      (d.clientEventId === req.eventId ||
        (d.clientEventId === null && now() - req.at <= APP_DELEGATION_WINDOW_MS))
    ) {
      origin = "app";
      appRequest = null;
    }
    liveDebug(`delegation (${origin})`, { id: d.id, offsetMs: d.offsetMs });
    if (nameStep) {
      nameOrigins.set(d.id, origin);
      const r = nameReply;
      // 氏名: 返事の後に live モデルが任せた。① を解いて結果を待ち (記録せずに終えたら ②)、
      // 応えが無いときのために ③ を張り直す
      if (origin === "live" && r && !r.settled && !r.appAsked) {
        r.liveDelegations.add(d.id);
        later("nameNet", QUIET_FALLBACK_MS, () => nameFallback("quiet"));
      }
    }
    const tn = turn;
    if (!tn || tn.committed) return;
    if (origin === "app") {
      tn.delegations.set(d.id, "app");
      return;
    }
    // live の経路は「窓の中で、相手が話した後に始まったもの」だけを今の設問のものとする。
    // **afterOpen は変異検証で等価と判定した (2026-09-14)** — 窓より前の発話は onInput が数えない
    // ので、相手が話した位置は必ず窓の開始より後にあり、afterUser が先に弾く。相手が話した位置の
    // 決め方 (onInput) を変えるときの網
    const afterOpen = tn.openedAt === null || d.offsetMs === null || d.offsetMs >= tn.openedAt;
    const afterUser =
      tn.userSpokeAt !== null && (d.offsetMs === null || tn.userSpokeAt <= d.offsetMs);
    if (!tn.sent || !afterOpen || !afterUser) return;
    tn.delegations.set(d.id, "live");
    tn.liveSinceInput = true;
    clear("t1");
  };

  const onCall = (c: LiveFunctionCall) => {
    // **結果は必ず返す** — 返さないと backend が続きを作れず止まる (2.1 の §7.4 の (2) と同じ)。
    // 捨てる tool にも返す
    client?.sendToolOutput(c.callId, JSON.stringify({ ok: true }));
    // tool を含んだ最初の応答の完了は数えず、その次 (続き) の完了まで次の依頼を待たせる
    if (c.delegationId)
      continuations.set(c.delegationId, { until: now() + CONTINUATION_WAIT_MS, skip: 1 });
    const tn = turn;
    if (tn && c.delegationId) tn.called.add(c.delegationId);
    let args: Record<string, unknown> = {};
    let invalid = false;
    try {
      const parsed: unknown = JSON.parse(c.args || "{}");
      args = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
    } catch {
      invalid = true;
    }
    // 握り潰さない。弾いた事実は画面が realtime.tool_call {outcome: "invalid"} で残す
    const reportInvalid = (message: string) => cb().onError({ code: "tool-invalid", message });
    const answerTool = c.name === "record_answer" || c.name === "need_repeat";
    if (purpose === "preflight" || !tn || !answerTool) {
      if (invalid) {
        reportInvalid(`不正な引数: ${c.name}`);
        return;
      }
      // 氏名の記録・聞き直しが届いたら、この返事への網は終わり
      if (nameStep && nameReply && (c.name === "confirm_name" || c.name === "need_repeat")) {
        nameReply.settled = true;
        clear("nameNet");
      }
      if (c.name === "need_repeat") {
        cb().onNeedRepeat("", toReason(args.reason));
        return;
      }
      // confirm_name など。engine は中身を解釈せず素通しし、次は画面が決める。経路 (via) は
      // delegation の出どころで決まる (氏名の step の間だけ分かる)
      const via = c.delegationId ? nameOrigins.get(c.delegationId) : undefined;
      if (via) cb().onToolCall?.(c.name, args, c.callId, { via });
      else cb().onToolCall?.(c.name, args, c.callId);
      return;
    }
    if (tn.committed) {
      cb().onToolIgnored?.(c.name, "duplicate");
      return;
    }
    const origin = c.delegationId ? tn.delegations.get(c.delegationId) : undefined;
    if (!origin) {
      cb().onToolIgnored?.(c.name, "stale");
      return;
    }
    clear("t1");
    clear("t3");
    // 記録にならなかった回は、止まらないよう経路ごとの次の手へ回す (live → アプリが頼む /
    // app → 待つのをやめる)。ここで戻るだけにすると、記録待ちのまま網も無音の出口も張られない
    if (invalid) {
      reportInvalid(`不正な引数: ${c.name}`);
      noRecord(tn, origin);
      return;
    }
    if (c.name === "need_repeat") {
      tn.repeatHandled = true;
      cb().onNeedRepeat(tn.question.id, toReason(args.reason));
      return;
    }
    const value = typeof args.value === "string" ? args.value.trim() : "";
    if (!value) {
      reportInvalid("record_answer に値が無い");
      noRecord(tn, origin);
      return;
    }
    tn.committed = true;
    const audio = merge(tn.pcm);
    tn.pcm = [];
    tn.pcmSamples = 0;
    if (audio.length > 0) cb().onUtteranceAudio?.(audio);
    setPhase("listening");
    cb().onAnswerCommitted(tn.question.id, value, { via: origin, heard: tn.heard.trim() });
  };

  const onBackendDone = (d: LiveBackendDone) => {
    if (d.usage) {
      usage.backendIn += d.usage.input;
      usage.backendCachedIn += d.usage.cached;
      usage.backendOut += d.usage.output;
      usage.backendResponses += 1;
    }
    if (d.delegationId) {
      const cont = continuations.get(d.delegationId);
      if (cont) {
        if (cont.skip > 0) {
          cont.skip -= 1;
        } else {
          continuations.delete(d.delegationId);
          schedule();
        }
      }
    }
    // ② (氏名) 返事の後に live モデルが任せたのに、backend が記録せずに終えた
    if (nameStep && d.delegationId && nameReply?.liveDelegations.has(d.delegationId))
      nameFallback("no-tool");
    const tn = turn;
    if (!tn || !d.delegationId || tn.committed || tn.called.has(d.delegationId)) return;
    const origin = tn.delegations.get(d.delegationId);
    // ② (live) 任せたのに backend が tool を呼ばずに終えた (spike の「回答の途中です」) → アプリが頼む。
    // (app) アプリが頼んだのに backend が記録を断った → 待つのをやめる (頼み直すと堂々巡りになる)
    if (origin) noRecord(tn, origin);
  };

  const onUsageSeconds = (s: number) => {
    usage.reportedSeconds = Math.max(usage.reportedSeconds, s);
  };

  const onClosed = (reason: string | null, seconds: number | null) => {
    if (seconds !== null) onUsageSeconds(seconds);
    // こちらが閉じた (close_requested) 以外は、会話を続けられない
    if (reason && reason !== "close_requested") lost(`session.closed: ${reason}`);
  };

  const onServerError = (e: { code: string; message: string }) => {
    // 上流の error は「送ったイベントが不正」などで、会話は続くことがある。画面は落とさず
    // ?realtime-debug=1 にだけ出す (止まる経路は無音の出口と二重の網が受け持つ)
    liveDebug("server error", e);
  };

  /** 費用の一次データを 1 回だけ出す。画面の送信処理が flush する前に出すため、stop の中で同期で出す */
  const emitUsage = () => {
    if (usageEmitted || !startedAt) return;
    usageEmitted = true;
    cb().onUsage?.({ elapsedSeconds: Math.round((now() - startedAt) / 100) / 10, ...usage });
  };

  const teardown = () => {
    seq += 1;
    emitUsage();
    const keys = [
      "t1",
      "t3",
      "pending",
      "narration",
      "speakStart",
      "userQuiet",
      "nameNet",
    ] as const;
    for (const k of keys) clear(k);
    if (poll !== null) {
      clearInterval(poll);
      poll = null;
    }
    silence?.stop();
    silence = null;
    mic?.stop();
    mic = null;
    if (pagehide) {
      window.removeEventListener("pagehide", pagehide);
      pagehide = null;
    }
    const tr = transport;
    // session.close を送ってから外す (送り先を先に外すと届かない)。閉じ忘れたセッションは
    // $0.05/分で課金され続ける
    client?.close();
    transport = null;
    client = null;
    // session.closed (最終の usage) を待つ猶予を置いてから接続を閉じる
    if (tr) setTimeout(() => tr.close(), CLOSE_GRACE_MS);
    started = false;
    onStarted = null;
    turn = null;
    pending = null;
    narration = null;
    appRequest = null;
    said = "";
    lastSaid = "";
    nameStep = false;
    nameReply = null;
    nameOrigins.clear();
    continuations.clear();
    tracker.reset();
    setPhase("idle");
  };

  const lost = (why: string) => {
    if (!transport) return;
    liveDebug("connection lost", why);
    teardown();
    cb().onError({ code: "connection-failed", message: why.slice(0, 200) });
  };

  const waitStarted = () =>
    new Promise<void>((resolve, reject) => {
      if (started) {
        resolve();
        return;
      }
      const h = setTimeout(() => {
        onStarted = null;
        reject(new Error("session.started が来ない"));
      }, STARTED_TIMEOUT_MS);
      onStarted = () => {
        clearTimeout(h);
        resolve();
      };
    });

  const pollLevel = async () => {
    const tr = transport;
    if (polling || !tr) return;
    polling = true;
    try {
      const level = await tr.remoteLevel();
      if (transport !== tr) return;
      onSpeech(tracker.feedLevel(level, now()));
      schedule();
    } catch {
      // 計測のために本編を止めない
    } finally {
      polling = false;
    }
  };

  const capture = (pcm: Float32Array) => {
    if (paused) return;
    const tn = turn;
    // 設問を頼む前の音声は前の設問への発話の続きなので、この設問の録音に混ぜない
    if (!tn || !tn.sent || tn.committed) return;
    tn.pcm.push(pcm);
    tn.pcmSamples += pcm.length;
    while (tn.pcmSamples > MAX_TURN_SAMPLES && tn.pcm.length > 1) {
      const dropped = tn.pcm.shift();
      tn.pcmSamples -= dropped?.length ?? 0;
    }
  };

  const start = async (opts?: StartOptions): Promise<void> => {
    if (transport) return; // 冪等 (連打で二重に繋がない)
    const my = ++seq;
    const live =
      <A extends unknown[]>(f: (...a: A) => void) =>
      (...a: A): void => {
        if (my === seq) f(...a);
      };
    purpose = opts?.purpose ?? "survey";
    usage = {
      reportedSeconds: 0,
      backendIn: 0,
      backendCachedIn: 0,
      backendOut: 0,
      backendResponses: 0,
    };
    usageEmitted = false;
    startedAt = 0;
    started = false;
    let stream: MediaStream;
    try {
      stream = await mediaStreamManager.ensureStream();
    } catch (e) {
      if (my === seq) cb().onError({ code: "connection-failed", message: errMsg(e) });
      return;
    }
    if (my !== seq) return; // await の間に stop が来た
    const c = new LiveClient({
      send: (p) => transport?.send(p),
      onSessionStarted: live(onSessionStarted),
      onInstructionsAppended: live(onAppended),
      onInputTranscript: live(onInput),
      onOutputTranscript: live(onOutput),
      onDelegationCreated: live(onDelegation),
      onFunctionCall: live(onCall),
      onBackendResponseDone: live(onBackendDone),
      onUsage: live(onUsageSeconds),
      onClosed: live(onClosed),
      onError: live(onServerError),
    });
    const tr = new LiveTransport({
      purpose,
      questions:
        purpose === "survey"
          ? (opts?.questions ?? []).map((q) => ({ id: q.id, text: q.text }))
          : undefined,
      stream,
      audio: liveAudioElement(),
      onMessage: (raw) => c.handle(raw),
      onClose: live(() => {
        if (transport === tr) lost("接続が切れた");
      }),
    });
    transport = tr;
    client = c;
    silence = new SilenceTimer({
      escalations: SILENCE_ESCALATIONS,
      onEscalation: (level) => cb().onSilence?.(level),
    });
    try {
      await tr.connect();
      if (my !== seq) return;
      await waitStarted();
      if (my !== seq) return;
    } catch (e) {
      if (my !== seq) return;
      // **ref を戻すのが要点** — 残すと冪等ガードに弾かれて二度と繋げなくなる (2.1 と同じ)
      transport = null;
      client = null;
      silence = null;
      try {
        tr.close();
      } catch {
        // 既に閉じている
      }
      cb().onError({ code: "connection-failed", message: errMsg(e) });
      return;
    }
    poll = setInterval(() => void pollLevel(), LEVEL_POLL_MS);
    if (purpose === "survey") {
      const m = new MicPcmSource();
      mic = m;
      m.start(stream, capture);
    }
    // ブラウザを閉じられたら接続を切る (課金は接続が切れるまで続く)
    const onHide = () => transport?.close();
    pagehide = onHide;
    window.addEventListener("pagehide", onHide);
    setPhase("listening");
    schedule(); // 起動前に頼まれていた依頼
  };

  const stop = () => {
    teardown();
  };

  const setQuestion = (q: Question) => {
    // 前の設問の網を解く。**変異検証で等価と判定した (2026-09-14)** — 残っても新しい設問では
    // まだ何も聞いていない (頼む中身が無い) ので fallback が何もせずに戻り、相手が話せば onInput が
    // t1 を解いて t3 を張り直す。fallback の「聞いていなければ頼まない」を外すときの網
    clear("t1");
    clear("t3");
    const tn = newTurn(q);
    turn = tn;
    request({
      content: buildQuestionAppend(q.text),
      expectUser: true,
      narration: false,
      onSent: (id) => {
        tn.sent = true;
        tn.appendEventId = id;
      },
    });
  };

  return {
    start,
    stop,
    pause: () => {
      paused = true;
      transport?.setSending(false);
    },
    resume: () => {
      paused = false;
      transport?.setSending(true);
    },
    setQuestion,
    narrate: (lines: string[]) => {
      // 案内の読み上げへ進んだ = 氏名の step を抜けた (氏名の網を畳む)
      nameStep = false;
      nameReply = null;
      clear("nameNet");
      request({ content: buildNarrationAppend(lines), expectUser: false, narration: true });
    },
    askName: () => {
      nameStep = true;
      nameReply = null;
      nameOrigins.clear();
      request({ content: buildNamePromptAppend(), expectUser: true, narration: false });
    },
    promptSilence: () =>
      request({
        content: buildSilenceAppend(),
        expectUser: true,
        narration: false,
        keepSilenceLevel: true,
      }),
    unlock: unlockLiveAudio,
  };
}

export function useLiveEngine(cb: EngineCallbacks): SpeechEngine {
  const cbRef = useRef(cb);
  cbRef.current = cb;
  const [state, setState] = useState<EngineState>("idle");
  const coreRef = useRef<LiveCore | null>(null);
  if (!coreRef.current) {
    coreRef.current = createLiveCore({
      cb: () => cbRef.current,
      setPhase: (s) => {
        setState(s);
        cbRef.current.onStateChange?.(s);
      },
    });
  }
  const core = coreRef.current;
  return useMemo(
    () => ({ ...core, kind: "live" as const, sessionMeta: SESSION_META, state }),
    [core, state]
  );
}
